import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import IconButton from './IconButton';

type FormPageV2Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Куда вести «Назад». Если строка — navigate(to); если функция — вызываем её. */
  backTo?: string | (() => void);
  /** Действия в правой части шапки (например, дополнительная кнопка). */
  headerActions?: ReactNode;
  /** Кнопки футера: «Отмена», «Сохранить». Sticky внизу. */
  footer?: ReactNode;
  /** Тело формы. Внутри MUI-компоненты автоматически перекрасятся под shell-тему. */
  children: ReactNode;
  /** Сообщение об ошибке поверх формы. */
  errorBanner?: ReactNode;
  /** Состояние загрузки исходных данных. */
  isLoading?: boolean;
  /** Максимальная ширина формы. По умолчанию 720px (комфортно читать). */
  maxWidth?: number | string;
};

/**
 * Эталон страницы-формы. Контейнер тот же, что у PageShell, но геометрия другая:
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │  ← Назад   Заголовок                       [Доп. кнопка]  │  header 56px
 *   ├───────────────────────────────────────────────────────────┤
 *   │                                                           │
 *   │   <form fields — ограничены maxWidth, выровнены по левому>│
 *   │                                                           │
 *   ├───────────────────────────────────────────────────────────┤
 *   │                            [Отмена]   [Сохранить]         │  footer 56px
 *   └───────────────────────────────────────────────────────────┘
 */
const FormPageV2 = ({
  title,
  subtitle,
  backTo,
  headerActions,
  footer,
  children,
  errorBanner,
  isLoading,
  maxWidth = 720,
}: FormPageV2Props) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (typeof backTo === 'function') backTo();
    else if (typeof backTo === 'string') navigate(backTo);
    else navigate(-1);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        background: 'var(--shell-bg-surface)',
        border: '1px solid var(--shell-border-subtle)',
        borderRadius: 'var(--shell-radius-lg)',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          borderBottom: '1px solid var(--shell-border-subtle)',
          flexShrink: 0,
          minHeight: 56,
        }}
      >
        <IconButton
          label="Назад"
          onClick={handleBack}
          icon={<ArrowLeft size={16} strokeWidth={1.75} />}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--shell-text-primary)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: 12,
                color: 'var(--shell-text-tertiary)',
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        {headerActions && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {headerActions}
          </div>
        )}
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: '20px 24px',
          background: 'var(--shell-bg-surface)',
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
              color: 'var(--shell-text-tertiary)',
              fontSize: 13,
            }}
          >
            Загрузка…
          </div>
        ) : (
          <div style={{ maxWidth, marginInline: 0 }}>
            {errorBanner && <div style={{ marginBottom: 12 }}>{errorBanner}</div>}
            {children}
          </div>
        )}
      </div>

      {/* Footer */}
      {footer && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
            padding: '12px 20px',
            borderTop: '1px solid var(--shell-border-subtle)',
            background: 'var(--shell-bg-surface)',
            flexShrink: 0,
            minHeight: 56,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export default FormPageV2;
